import React from 'react'
import { createChart, type IChartApi, type LineData } from 'lightweight-charts'
import type { TF } from '../types'
import { rsiFromCandles } from '../utils/indicators'
import { normalizeCandles } from '../utils/normalize'

type RSIPaneProps = {
  candles: Array<{ time: number | any; open: number; high: number; low: number; close: number }>
  timeframe: TF
  length?: number
  className?: string
  showMA?: boolean
  syncTo?: IChartApi | null
  syncLen?: number
  externalVisibleRange?: { from: number; to: number } | null
}

export default function RSIPane({ candles, timeframe, length = 14, className, showMA = true, syncTo, syncLen, externalVisibleRange }: RSIPaneProps) {
  const baseCandles = React.useMemo(() => { try { const arr = Array.isArray(candles) ? (candles as any[]) : []; if (!arr.length) return [] as any[]; const hasTime = typeof arr[0]?.time !== 'undefined'; return hasTime ? arr : normalizeCandles(arr as any, timeframe) } catch { return [] as any[] } }, [candles, timeframe]);
  const rsiDataRaw = React.useMemo<LineData[]>(() => rsiFromCandles(baseCandles as any, length), [baseCandles, length])
  // Alinha o RSI para ter a MESMA linha do tempo do grÃ¡fico principal (preenche inÃ­cio com whitespace)
  const rsiData = React.useMemo<any[]>(() => {
    try {
      const out: any[] = []
      const map = new Map<number, number>()
      for (const d of rsiDataRaw) map.set(Number(d.time as any), Number(d.value))
      for (const c of baseCandles as any[]) {
        const t = Number(c?.time)
        if (!Number.isFinite(t)) continue
        const v = map.get(t)
        if (Number.isFinite(v as any)) out.push({ time: t as any, value: v })
        else out.push({ time: t as any } as any) // whitespace: mantÃ©m alinhamento sem valor
      }
      return out
    } catch {
      return rsiDataRaw as any
    }
  }, [rsiDataRaw, baseCandles])
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const chartRef = React.useRef<IChartApi | null>(null)
  const rsiRef = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)
  const maRef = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)
  const bandRef = React.useRef<HTMLDivElement | null>(null)
  const r70Ref = React.useRef<HTMLDivElement | null>(null)
  const r50Ref = React.useRef<HTMLDivElement | null>(null)
  const r30Ref = React.useRef<HTMLDivElement | null>(null)
  const rafRef = React.useRef<number | null>(null)
  const [dataAppliedTick, setDataAppliedTick] = React.useState(0)

  const updateBand = React.useCallback(() => {
    try {
      const y70 = rsiRef.current?.priceToCoordinate(70)
      const y30 = rsiRef.current?.priceToCoordinate(30)
      if (bandRef.current && typeof y70 === 'number' && typeof y30 === 'number') {
        const top = Math.min(y70, y30)
        const height = Math.abs(y30 - y70)
        bandRef.current.style.top = `${top}px`
        bandRef.current.style.height = `${height}px`
      }
      const y50 = rsiRef.current?.priceToCoordinate(50)
      if (typeof y70 === 'number' && r70Ref.current) r70Ref.current.style.top = `${y70}px`
      if (typeof y50 === 'number' && r50Ref.current) r50Ref.current.style.top = `${y50}px`
      if (typeof y30 === 'number' && r30Ref.current) r30Ref.current.style.top = `${y30}px`
    } catch {}
  }, [])

  React.useEffect(() => {
    const el = containerRef.current
    if (!el || chartRef.current) return
    const chart = createChart(el, {
      layout: { background: { color: 'transparent' }, textColor: 'rgba(255,255,255,0.75)' },
      rightPriceScale: { borderColor: 'transparent', scaleMargins: { top: 0.05, bottom: 0.05 } },
      // Alinha experiÃªncia com TV: offset e espaÃ§amento iguais ao principal
      timeScale: { borderColor: 'transparent', borderVisible: false, visible: false, timeVisible: true, fixLeftEdge: false, rightBarStaysOnScroll: false, rightOffset: rightOffsetBars(timeframe), barSpacing: defaultSpacing(timeframe) } as any,
      grid: { vertLines: { visible: false, color: 'transparent' }, horzLines: { visible: false, color: 'transparent' } },
      crosshair: { mode: 0 },
      // Permite pan com arrasto, mas bloqueia zoom exceto nas rÃ©guas
      handleScroll: { pressedMouseMove: false, mouseWheel: false, horzTouchDrag: false, vertTouchDrag: true } as any,
      handleScale: { mouseWheel: false, pinch: false, axisPressedMouseMove: { time: false, price: true }, axisDoubleClickReset: { time: false, price: true } } as any,
      autoSize: true,
    })
    const rsi = chart.addLineSeries({ color: '#8b5cf6', lineWidth: 2, priceLineVisible: false })
    const ma = chart.addLineSeries({ color: '#f59e0b', lineWidth: 1, priceLineVisible: false, visible: showMA })
    chartRef.current = chart
    rsiRef.current = rsi
    maRef.current = ma
    const ro = new ResizeObserver(() => { chart.applyOptions({ autoSize: true }); updateBand() })
    ro.observe(el)
    const loop = () => { updateBand(); rafRef.current = requestAnimationFrame(loop) }
    rafRef.current = requestAnimationFrame(loop)
    return () => {
      try { if (rafRef.current) cancelAnimationFrame(rafRef.current) } catch {}
      rafRef.current = null
      try { ro.disconnect() } catch {}
      try { chart.remove() } catch {}
      chartRef.current = null; rsiRef.current = null; maRef.current = null
    }
  }, [showMA, updateBand, timeframe])

  // SincronizaÃ§Ã£o horizontal com preservaÃ§Ã£o de offset (TV-like)
  React.useEffect(() => { return }, [syncTo, updateBand, timeframe, rsiData, syncLen])
  React.useEffect(() => {
    const main = syncTo
    const rsi = chartRef.current
    const hasRsiData = Array.isArray(rsiData) && rsiData.length > 0
    if (!main || !rsi || !hasRsiData) return

    // aplica faixa inicial do principal para o RSI (preferir logical range)
    try {
      const lr = (main.timeScale() as any).getVisibleLogicalRange?.()
      if (lr && isFinite(lr.from) && isFinite(lr.to)) {
        (rsi.timeScale() as any).setVisibleLogicalRange(lr)
      } else {
        const tr = (main.timeScale() as any).getVisibleRange?.()
        const fromOk = typeof (tr?.from) === 'number'
        const toOk = typeof (tr?.to) === 'number'
        if (fromOk && toOk) (rsi.timeScale() as any).setVisibleRange(tr)
      }
    } catch {}

    const onMainLogical = (range: any) => {
      if (!range) return
      try { (rsi.timeScale() as any).setVisibleLogicalRange(range) } catch {}
    }
    try { (main.timeScale() as any).subscribeVisibleLogicalRangeChange(onMainLogical) } catch {}

    return () => {
      try { (main.timeScale() as any).unsubscribeVisibleLogicalRangeChange(onMainLogical) } catch {}
    }
  }, [syncTo, timeframe, syncLen, rsiData.length, dataAppliedTick])

  // Sincronização por range externo (ex.: TradingView widget)
  React.useEffect(() => {
    try {
      const rsi = chartRef.current
      if (!rsi) return
      if (!externalVisibleRange) return
      const norm = (v: number) => (v > 2e10 ? Math.floor(v / 1000) : Math.floor(v))
      const from = norm(Number(externalVisibleRange.from))
      const to = norm(Number(externalVisibleRange.to))
      if (Number.isFinite(from) && Number.isFinite(to)) {
        ;(rsi.timeScale() as any).setVisibleRange({ from, to })
      }
    } catch {}
  }, [externalVisibleRange?.from, externalVisibleRange?.to])

  React.useEffect(() => {
    if (!chartRef.current || !rsiRef.current) return
    const src: any[] = rsiData as any
    let lastIdx = -1
    for (let i = src.length - 1; i >= 0; i--) { if (Number.isFinite((src[i] as any).value)) { lastIdx = i; break } }
    const trimmed = lastIdx >= 0 ? src.slice(0, lastIdx + 1) : src
    rsiRef.current.setData(trimmed)
    if (maRef.current) {
      const window = Math.max(1, Math.floor(length))
      const vals = trimmed.filter((p: any) => Number.isFinite(p?.value))
      const arr: LineData[] = []
      for (let i = window - 1; i < vals.length; i++) {
        let sum = 0
        for (let j = i - window + 1; j <= i; j++) sum += Number(vals[j].value)
        arr.push({ time: vals[i].time, value: sum / window })
      }
      maRef.current.setData(arr)
      maRef.current.applyOptions({ visible: showMA })
    }
    try {
      // Amplia visualmente a zona 30–70 ao focar o range 20–80
      rsiRef.current.applyOptions({ autoscaleInfoProvider: () => ({ priceRange: { minValue: 20, maxValue: 80 } }) } as any)
      maRef.current?.applyOptions({ autoscaleInfoProvider: () => ({ priceRange: { minValue: 20, maxValue: 80 } }) } as any)
      updateBand()
    } catch {}
    try { setDataAppliedTick((t)=>t+1) } catch {}
  }, [rsiData, length, showMA])

  const base = 'relative overflow-hidden w-full';
  return (
    <div className={`${base} ${className || 'h-40'} border-t border-border`}>
      <div ref={bandRef} className="absolute left-0 right-0" style={{ background: 'rgba(148,163,184,0.08)', pointerEvents: 'none' }} />
      <div ref={r70Ref} className="absolute left-0 right-0" style={{ borderTop: '1px dashed rgba(148,163,184,0.35)', pointerEvents: 'none' }} />
      <div ref={r50Ref} className="absolute left-0 right-0" style={{ borderTop: '1px dashed rgba(148,163,184,0.25)', pointerEvents: 'none' }} />
      <div ref={r30Ref} className="absolute left-0 right-0" style={{ borderTop: '1px dashed rgba(148,163,184,0.35)', pointerEvents: 'none' }} />
      <div ref={containerRef} className="absolute inset-0" />
    </div>
  )
}

function defaultSpacing(tf: TF): number {
  switch (tf) {
    case '5': return 6
    case '15': return 10
    case '30': return 9
    case '60': return 8
    default: return 8
  }
}

function rightOffsetBars(tf: TF): number {
  switch (tf) {
    case "W": return 3;
    case "M": return 1;
    default: return 12;
  }
}
function tfToSeconds(tf: TF): number {
  switch (tf) {
    case '5': return 300
    case '15': return 900
    case '30': return 1800
    case '60': return 3600
    case '240': return 14400
    case '720': return 43200
    case 'D': return 86400
    case 'W': return 604800
    default: return 2592000
  }
}










